import React, {useEffect, useState} from 'react';
import {DateRangePicker, DropdownList, ButtonDropdownField, Icon, FlexRow} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {
	getPreviousMonthRange,
	getMonthRange,
	dateObjectToIsoDate,
	getNextMonthRange,
} from '../../forecast-app/shared/util/DateUtil';

export type DatePickerOnChange = {
	startDate: string | null;
	endDate: string | null;
};

type PredefinedDateRangePickerProps = {
	startDate: string | null;
	endDate: string | null;
	onChange: (onChangeEvent: DatePickerOnChange) => void;
};

const PredefinedDateRangePicker = ({startDate, endDate, onChange}: PredefinedDateRangePickerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const date = new Date();

	const preselectedRanges = [
		{name: formatMessage({id: 'common.previous_month'}), range: getPreviousMonthRange(date)},
		{name: formatMessage({id: 'common.current_month'}), range: getMonthRange(date)},
		{name: formatMessage({id: 'date_span_picker.next_month'}), range: getNextMonthRange(date)},
		{name: formatMessage({id: 'common.all_time'}), range: null},
	];

	const [rangeIndex, setRangeIndex] = useState<number | null>(null);

	const setDateRange = (index: number) => {
		setRangeIndex(index);
		const dateRange = preselectedRanges[index].range;
		onChange({startDate: dateObjectToIsoDate(dateRange?.startDate), endDate: dateObjectToIsoDate(dateRange?.endDate)});
	};

	const setCustomDate = (range: DatePickerOnChange) => {
		setRangeIndex(null);
		onChange(range);
	};

	useEffect(() => {
		if (startDate && endDate) {
			const preselectedRangeIndex = preselectedRanges.findIndex(
				preselectedRange =>
					dateObjectToIsoDate(preselectedRange.range?.startDate) === startDate &&
					dateObjectToIsoDate(preselectedRange.range?.endDate) === endDate
			);
			if (preselectedRangeIndex !== -1) {
				setDateRange(preselectedRangeIndex);
			}
		} else {
			setDateRange(3);
		}
	}, []);

	return (
		<>
			<DropdownList>
				<DropdownList.Trigger>
					<ButtonDropdownField width="180px" data-cy={'predefined-date-range-picker'}>
						{rangeIndex === null ? (
							<FormattedMessage id={'common.custom_range'} />
						) : (
							preselectedRanges[rangeIndex].name
						)}
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={10} align={'start'} width="199px">
					{preselectedRanges.map((range, index) => (
						<DropdownList.Item
							key={index}
							onClick={() => setDateRange(index)}
							data-cy={'predefined-date-range-picker-' + index}
						>
							<FlexRow justifyContent={'space-between'}>
								<div>{range.name}</div>
								{index === rangeIndex && <Icon icon="tick"></Icon>}
							</FlexRow>
						</DropdownList.Item>
					))}
				</DropdownList.Content>
			</DropdownList>
			<DateRangePicker
				startDate={startDate}
				endDate={endDate}
				onChange={setCustomDate}
				aria-label="Select date range"
				data-cy={'date-range-picker'}
			/>
		</>
	);
};

export default PredefinedDateRangePicker;
